import { useCallback, useEffect, useMemo, useState } from 'react'

import { LANGUAGE_FROM_TOLGEE } from 'consts/languageFromTolgee'
import type { TEntityLanguages } from 'entities/languages'
import { useCurrentLanguage, useTranslate } from 'lib/tolgee'
import { UserStore } from 'stores'
import { TOLGEE_GLOBALS } from 'utils'

export interface LanguageOption {
  value: TEntityLanguages
  label: string
  disabled: boolean
}

export function useLanguageOptions() {
  const t = useTranslate()
  const tolgeeSelectedLanguage = useCurrentLanguage()

  const userLanguageOptions: LanguageOption[] = UserStore.userLanguageOptions
  const userLanguageOptionsTranslated: LanguageOption[] = userLanguageOptions.map(language => {
    return {
      ...language,
      label: t(TOLGEE_GLOBALS?.[language?.value]),
    }
  })
  const languageOptionsAvailable = userLanguageOptions
    .filter(language => !language.disabled)
    .sort((a, b) => {
      return a.label.localeCompare(b.label)
    })
  const firstlanguageOptionsAvailable = languageOptionsAvailable[0]

  const initialLanguage = useMemo(() => {
    return (
      languageOptionsAvailable.find(language => {
        return language.value === LANGUAGE_FROM_TOLGEE[tolgeeSelectedLanguage]
      }) || firstlanguageOptionsAvailable
    )
  }, [firstlanguageOptionsAvailable, languageOptionsAvailable, tolgeeSelectedLanguage])

  const [selectedLanguage, setSelectedLanguage] = useState<LanguageOption | undefined>(initialLanguage)

  useEffect(() => {
    setSelectedLanguage(initialLanguage)
  }, [initialLanguage])

  const selectLanguage = useCallback(
    (language: LanguageOption) => {
      setSelectedLanguage(language)
    },
    [setSelectedLanguage]
  )

  const memoizedValues = useMemo(
    () => ({
      languageOptions: userLanguageOptionsTranslated,
      selectedLanguage,
      selectLanguage,
    }),
    [selectLanguage, selectedLanguage, userLanguageOptionsTranslated]
  )

  return memoizedValues
}
